import type { DeviceReportedSettings } from "./reported.js";

/**
 * Ob ein Gerät als erreichbar gilt.
 *
 * **Der gespeicherte Online-Zustand allein ist keine Antwort.** Er ist das
 * Ergebnis der letzten Nachricht, die jemand entgegengenommen hat: Das Gerät
 * meldet sich (`hello`, `status`), oder der Broker meldet in seinem Namen das
 * Testament. Bleibt beides aus — weil das Gerät stromlos ist _und_ der Ingest
 * gerade nicht lief, weil der Broker neu gestartet ist, weil das Netz zwischen
 * beiden weg war —, dann steht der Wert einfach still. Er ist dann nicht falsch
 * geschrieben, sondern nie widerrufen worden, und das ist derselbe Zustand.
 *
 * Damit hinge die Anzeige „erreichbar" daran, dass ein Dienst lückenlos gelaufen
 * ist. Ein Gerät stand so mit „Letzte Meldung: vor 11 Stunden" als verbunden da,
 * bei einem Sendetakt von 60 Sekunden — die Angabe daneben widerlegte die Marke.
 *
 * Deshalb wird gegengerechnet: Erreichbar ist, wer verbunden **gemeldet** ist
 * **und** sich innerhalb seiner Frist zuletzt gemeldet hat. Das ist keine zweite
 * Quelle, sondern eine Verfallsprüfung an der einen — sie kann einem Gerät die
 * Erreichbarkeit nur **nehmen**, nie geben: Ein Gerät für erreichbar zu
 * erklären, von dem nichts vorliegt, wäre Raten (Regel 9).
 *
 * Was dabei herauskommt, ist kein Boolean, sondern `DeviceReachability` — der
 * Grund steht dort.
 */

/**
 * Wie viele Sendetakte ausbleiben dürfen, bevor ein Gerät als still gilt.
 *
 * Drei, nicht einer: Ein einzelnes verlorenes Paket ist bei WLAN im Keller der
 * Normalfall und kein Ausfall. Wer hier `1` setzte, bekäme eine Marke, die im
 * Minutentakt hin- und herspringt — und die nach dem zweiten Mal niemand mehr
 * liest.
 */
export const PRESENCE_GRACE_FACTOR = 3;

/**
 * Die Untergrenze der Frist, unabhängig vom gemeldeten Takt.
 *
 * Sie gilt aus zwei Richtungen. Ein Gerät mit sehr kurzem Sendetakt — die
 * Firmware lässt bis herunter zu einer Sekunde zu — spränge sonst bei jeder
 * kleinen Störung um. Und ein Gerät, das **keinen** Takt gemeldet hat, braucht
 * überhaupt eine Frist: Aus dem Schweigen „sendet nie" abzuleiten wäre so falsch
 * wie „sendet ständig".
 */
export const PRESENCE_MIN_GRACE_SECONDS = 300;

/** Was zur Beurteilung nötig ist — mehr braucht die Frage nicht. */
export interface DevicePresence {
  readonly online: boolean;
  readonly lastSeenAt: Date | null;
  readonly reportedSettings: DeviceReportedSettings | null;
}

/**
 * Wie lange ein Lebenszeichen ausbleiben darf.
 *
 * `cloudInterval` ist eine **Zeichenkette** — der Wortlaut der lokalen REST-API
 * des Geräts, hier nicht umgedeutet (`DeviceReportedSettings`). Gerechnet wird
 * deshalb an dieser Stelle, und was sich nicht als positive Zahl lesen lässt,
 * gilt als nicht gemeldet: Aus `"auto"` eine `0` zu machen hieße, jedes Gerät
 * dieser Firmware dauerhaft für still zu erklären.
 */
export function presenceGraceSeconds(settings: DeviceReportedSettings | null): number {
  const interval = Number(settings?.cloudInterval);
  const base =
    !Number.isFinite(interval) || interval <= 0
      ? PRESENCE_MIN_GRACE_SECONDS
      : Math.max(interval * PRESENCE_GRACE_FACTOR, PRESENCE_MIN_GRACE_SECONDS);
  /*
   * Ein Gerät, das seinen Zähler abfragen muss (HM-192), sendet Telemetrie nur
   * nach einer Auslesung — alle zwölf Stunden. Sein Lebenszeichen ist dann
   * allein `status`, und das kommt alle 300 Sekunden: eine Frist von 300 hieße,
   * dass ein einziges verspätetes `status` das Gerät „überfällig" macht.
   */
  return readInterval(settings) === null ? base : Math.max(base, STATUS_HEARTBEAT_SECONDS * PRESENCE_GRACE_FACTOR);
}

/**
 * Das Lebenszeichen `status` der Firmware (`CTS_Node/docs/CLOUD.md`): alle 300 Sekunden,
 * unabhängig vom Sendetakt der Messwerte.
 */
export const STATUS_HEARTBEAT_SECONDS = 300;

/**
 * Der Auslesetakt in Sekunden — **nur** bei einem Zähler, der abgefragt werden muss.
 *
 * `null` heißt: nicht gemeldet, das Gerät liest laufend. `0` heißt: nur auf Anforderung,
 * es gibt keinen Takt, gegen den sich ein Ausbleiben messen ließe. Wie bei
 * `cloudInterval` ist das Feld eine Zeichenkette, und was sich nicht als Zahl lesen
 * lässt, gilt als nicht gemeldet.
 */
export function readInterval(settings: DeviceReportedSettings | null): number | null {
  const raw = settings?.readInterval;
  if (raw === undefined) return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * In welchem Abstand die Werte eines Geräts zu erwarten sind (#99) — der
 * Auslesetakt, wenn es einen gibt, sonst `null`.
 *
 * Ein Verlauf bricht seine Linie an einer **ausgebliebenen Auslesung** ab, nicht an
 * einem leeren Abschnitt des Rasters: Ein Wärmemengenzähler, der alle 15 Minuten
 * gelesen wird, hat im Fünf-Minuten-Raster zwei leere Abschnitte zwischen je zwei
 * Werten, und keiner davon ist ein Ausfall. `0` (nur auf Anforderung) gibt keinen
 * Takt her, gegen den sich ein Ausbleiben messen ließe — dann gilt das Raster.
 */
export function readingCadence(settings: DeviceReportedSettings | null): number | null {
  const seconds = readInterval(settings);
  return seconds === null || seconds === 0 ? null : seconds;
}

/**
 * Wie es um die Erreichbarkeit eines Geräts steht.
 *
 * **Vier Zustände aus zwei Quellen**, und beide liegen ohnehin vor: der zuletzt
 * entgegengenommene Verbindungszustand und der Zeitpunkt des letzten
 * Lebenszeichens. Ein Boolean wirft die Hälfte davon weg — es sagt „still" und
 * verschweigt, ob die Verbindung **nachweislich** abgerissen ist oder ob sie am
 * Broker weiter als bestehend gilt und trotzdem nichts kommt. Das sind zwei
 * verschiedene Lagen: Die eine ist ein abgebautes Gerät, die andere ein
 * hängendes, ein Netzausfall oder ein Broker, der nach einem Neustart eine alte
 * behaltene Nachricht ausliefert.
 *
 * Keine zusätzliche Frist trennt sie — sie stehen für Verschiedenes und nicht
 * für „länger her".
 */
export const DEVICE_REACHABILITIES = ["reachable", "overdue", "unreachable", "never_seen"] as const;
export type DeviceReachability = (typeof DEVICE_REACHABILITIES)[number];

/**
 * Der Zustand zum Zeitpunkt `at`.
 *
 * **Ohne Lebenszeichen entscheidet der gemeldete Zustand.** Eine Frist braucht
 * einen Zeitpunkt, von dem an sie läuft. Fehlt er, gibt es zwei Fälle und keinen
 * dritten: Der Broker hat eine behaltene `status` zugestellt — dann ist deren
 * `online` der jüngste Stand, den es gibt —, oder es liegt gar nichts vor, und
 * das ist ein frisch gekoppeltes Gerät und kein Schaden.
 *
 * **Eine gemeldete Abmeldung braucht hier keinen eigenen Zweig.** `unlink` setzt
 * „still", und die Meldung kam live — der Zustand ist damit `unreachable`, und
 * warum, sagt der Hinweis daneben (OQ-023).
 */
export function deviceReachability(device: DevicePresence, at: Date): DeviceReachability {
  if (!device.online) return device.lastSeenAt === null ? "never_seen" : "unreachable";
  if (device.lastSeenAt === null) return "reachable";

  const silentMs = at.getTime() - device.lastSeenAt.getTime();
  const overdue = silentMs > presenceGraceSeconds(device.reportedSettings) * 1000;
  return overdue ? "overdue" : "reachable";
}

/**
 * Ob das Gerät gerade Daten liefert.
 *
 * Die grobe Frage für alles, was keine Abstufung braucht — eine Auswertung will
 * wissen, ob Werte kommen, nicht warum sie ausbleiben.
 */
export function isReachable(device: DevicePresence, at: Date): boolean {
  return deviceReachability(device, at) === "reachable";
}

/** Was zur Frage nach den Messwerten zusätzlich nötig ist. */
export interface DeviceReadingPresence extends DevicePresence {
  /** Wann zuletzt eine Nachricht mit mindestens einem geschriebenen Wert ankam. */
  readonly lastReadingAt: Date | null;
}

/**
 * Ob ein erreichbares Gerät keine Messwerte mehr liefert (HM-179).
 *
 * **Ein Lebenszeichen ist kein Messwert.** Der Lesekopf meldet seinen Zustand
 * alle paar Minuten, auch wenn er am Zähler nichts liest — verrutscht, die
 * Info-Schnittstelle des Zählers gesperrt, das Kabel zur Zusatzschnittstelle
 * gezogen. Er stand dann dauerhaft als „erreichbar" da, und unter „Strom &
 * Erzeugung" fehlten Werte, ohne dass irgendwo stand, warum.
 *
 * **Dieselbe Frist wie die Erreichbarkeit** (`presenceGraceSeconds`): Werte
 * kommen im selben Sendetakt wie die Lebenszeichen, eine zweite Zahl wäre eine
 * zweite Meinung über denselben Takt.
 *
 * **Nur an einem erreichbaren Gerät.** Ist es überfällig oder getrennt, fehlen
 * die Werte aus diesem Grund, und der Hinweis dazu sagt das schon. Und **nie an
 * einem Gerät, das noch keinen Wert geliefert hat**: Ein frisch gekoppeltes oder
 * eines ohne bestückten Zähler hat nichts verloren — aus dem Fehlen eines
 * Zeitpunkts einen Ausfall abzuleiten wäre Raten (Regel 9).
 */
export function readingsStalled(device: DeviceReadingPresence, at: Date): boolean {
  if (device.lastReadingAt === null) return false;
  if (deviceReachability(device, at) !== "reachable") return false;
  /*
   * Ein Zähler, der abgefragt wird (HM-192), liefert im Takt seiner Auslesung —
   * dieselbe Frist wie die Erreichbarkeit wäre nach fünf Minuten „keine
   * Messwerte". Ohne Takt (`0`, nur auf Anforderung) gibt es nichts, wogegen
   * sich ein Ausbleiben messen ließe: keine Aussage statt einer geratenen.
   */
  const reading = readInterval(device.reportedSettings);
  if (reading === 0) return false;
  const grace =
    reading === null
      ? presenceGraceSeconds(device.reportedSettings)
      : Math.max(reading * PRESENCE_GRACE_FACTOR, presenceGraceSeconds(device.reportedSettings));
  const silentMs = at.getTime() - device.lastReadingAt.getTime();
  return silentMs > grace * 1000;
}

/**
 * Wonach eine Übersicht über viele Geräte zählt und filtert (#89): die vier
 * Zustände der Erreichbarkeit und dazu „liefert keine Messwerte".
 *
 * **Der fünfte ist keine Erreichbarkeit, sondern ein Teil von `reachable`**
 * (`readingsStalled` gilt nur dort). Er steht trotzdem in derselben Liste, weil
 * er dieselbe Frage beantwortet — „läuft es?" —, und ein Gerät, das sich
 * meldet und nichts liest, ist für den Betrieb ein Ausfall wie ein stummes.
 * Die Zahlen addieren sich deshalb nicht zur Gesamtzahl; die vier ersten tun es.
 */
export const DEVICE_PRESENCE_STATES = [...DEVICE_REACHABILITIES, "readings_stalled"] as const;
export type DevicePresenceState = (typeof DEVICE_PRESENCE_STATES)[number];

/** Was zum Zählen gebraucht wird — schon gerechnet, nicht die Rohspalten. */
export interface DevicePresenceSummary {
  readonly reachability: DeviceReachability;
  readonly readingsStalled: boolean;
}

export function hasPresenceState(device: DevicePresenceSummary, state: DevicePresenceState): boolean {
  return state === "readings_stalled" ? device.readingsStalled : device.reachability === state;
}

export type DevicePresenceTally = Readonly<Record<DevicePresenceState | "total", number>>;

export function tallyPresence(devices: readonly DevicePresenceSummary[]): DevicePresenceTally {
  const tally: Record<DevicePresenceState | "total", number> = {
    total: devices.length,
    reachable: 0,
    overdue: 0,
    unreachable: 0,
    never_seen: 0,
    readings_stalled: 0,
  };
  for (const device of devices) {
    for (const state of DEVICE_PRESENCE_STATES) {
      if (hasPresenceState(device, state)) tally[state] += 1;
    }
  }
  return tally;
}
